import { useState, useEffect } from "react";
import { services } from "../../api";
import { Land, LandsListContent } from "../../interfaces/api/lands";

interface UseLandsResult {
  lands: Land[];
  totalItems: number;
  currentPage: number;
  perPage: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchLands: (page: number) => Promise<void>;
}

export const useLands = (initialPage: number = 1): UseLandsResult => {
  const [lands, setLands] = useState<Land[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [perPage, setPerPage] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const fetchLands = async (page: number = 1): Promise<void> => {
    try {
      if (loading || error) return; // Don't fetch if already loading or if there's an error
      setLoading(true);
      setError(null);

      const response = await services.lands.getLands({ page });
      const content = response.content as LandsListContent;

      if (page === 1) {
        setLands(content.data);
      } else {
        setLands((prevLands) => [...prevLands, ...content.data]);
      }

      setTotalItems(content.total);
      setCurrentPage(content.current_page);
      setPerPage(content.per_page);

      // Update hasMore based on whether we've loaded all items
      setHasMore(lands.length + content.data.length < content.total);
    } catch (err) {
      setError("Failed to fetch lands");
      console.error("Error fetching lands:", err);
      setHasMore(false); // Stop infinite scroll on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLands(initialPage);
  }, [initialPage]);

  return {
    lands,
    totalItems,
    currentPage,
    perPage,
    loading,
    error,
    hasMore,
    fetchLands,
  };
};
